import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Lightbulb } from 'lucide-react';

export type ExplainContext =
  | 'home'
  | 'unit'
  | 'explore'
  | 'host'
  | 'info'
  | 'chat'
  | 'assistant'
  | 'modal-wifi'
  | 'modal-notifications'
  | 'modal-navigation'
  | 'modal-payment'
  | 'modal-arrival'
  | 'modal-share';

interface DemoExplainTooltipProps {
  context: ExplainContext | null;
  isDesktop: boolean;
  /** Reserved for future theming hooks. */
  darkMode?: boolean;
  /** Auto-hide after this many ms. Pass 0 to disable. */
  autoHideMs?: number;
}

const CONTEXT_KEYS: Record<
  ExplainContext,
  { titleKey: string; bodyKey: string; titleFallback: string; bodyFallback: string }
> = {
  home: {
    titleKey: 'demo.explain.tabHomeTitle',
    bodyKey: 'demo.explain.tabHomeBody',
    titleFallback: 'Početna',
    bodyFallback: 'Sve o boravku na jednom mjestu.',
  },
  unit: {
    titleKey: 'demo.explain.tabUnitTitle',
    bodyKey: 'demo.explain.tabUnitBody',
    titleFallback: 'Smještaj',
    bodyFallback: 'Galerija i detalji.',
  },
  explore: {
    titleKey: 'demo.explain.tabExploreTitle',
    bodyKey: 'demo.explain.tabExploreBody',
    titleFallback: 'Istraži',
    bodyFallback: 'Vaše preporuke.',
  },
  host: {
    titleKey: 'demo.explain.tabHostTitle',
    bodyKey: 'demo.explain.tabHostBody',
    titleFallback: 'Domaćin',
    bodyFallback: 'Vaše objave i usluge.',
  },
  info: {
    titleKey: 'demo.explain.tabInfoTitle',
    bodyKey: 'demo.explain.tabInfoBody',
    titleFallback: 'Info',
    bodyFallback: 'Pravila i postavke.',
  },
  chat: {
    titleKey: 'demo.explain.chatTitle',
    bodyKey: 'demo.explain.chatBody',
    titleFallback: 'Chat',
    bodyFallback: 'Razgovor s gostom.',
  },
  assistant: {
    titleKey: 'demo.explain.assistantTitle',
    bodyKey: 'demo.explain.assistantBody',
    titleFallback: 'AI Asistent',
    bodyFallback: 'AI odgovara na pitanja.',
  },
  'modal-wifi': {
    titleKey: 'demo.explain.modalWifiTitle',
    bodyKey: 'demo.explain.modalWifiBody',
    titleFallback: 'WiFi',
    bodyFallback: 'Klikom kopira podatke.',
  },
  'modal-notifications': {
    titleKey: 'demo.explain.modalNotificationsTitle',
    bodyKey: 'demo.explain.modalNotificationsBody',
    titleFallback: 'Obavijesti',
    bodyFallback: 'Status u jedan klik.',
  },
  'modal-navigation': {
    titleKey: 'demo.explain.modalNavigationTitle',
    bodyKey: 'demo.explain.modalNavigationBody',
    titleFallback: 'Navigacija',
    bodyFallback: 'GPS i upute.',
  },
  'modal-payment': {
    titleKey: 'demo.explain.modalPaymentTitle',
    bodyKey: 'demo.explain.modalPaymentBody',
    titleFallback: 'Plaćanje',
    bodyFallback: 'Status uplata.',
  },
  'modal-arrival': {
    titleKey: 'demo.explain.modalArrivalTitle',
    bodyKey: 'demo.explain.modalArrivalBody',
    titleFallback: 'Self check-in',
    bodyFallback: 'Lockbox / keypad / smart lock.',
  },
  'modal-share': {
    titleKey: 'demo.explain.modalShareTitle',
    bodyKey: 'demo.explain.modalShareBody',
    titleFallback: 'Podijeli',
    bodyFallback: 'Pošalji link grupi.',
  },
};

export function DemoExplainTooltip({
  context,
  isDesktop,
  autoHideMs = 0,
}: DemoExplainTooltipProps) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when context changes
  useEffect(() => {
    setDismissed(false);
  }, [context]);

  // Auto-hide
  useEffect(() => {
    if (!autoHideMs || dismissed || !context) return;
    const timer = setTimeout(() => setDismissed(true), autoHideMs);
    return () => clearTimeout(timer);
  }, [context, autoHideMs, dismissed]);

  if (!context || dismissed) return null;

  const { titleKey, bodyKey, titleFallback, bodyFallback } = CONTEXT_KEYS[context];
  const title = t(titleKey, titleFallback);
  const body = t(bodyKey, bodyFallback);

  // Higher z-index than modals (modals are z-60) so tooltip is always visible
  const zIndex = 'z-[80]';

  // Desktop: fixed side card to the right of the phone frame (centered horizontally on the right side)
  if (isDesktop) {
    return (
      <div
        key={context}
        className={`fixed top-1/2 -translate-y-1/2 max-w-xs animate-in fade-in slide-in-from-left-4 duration-300 ${zIndex}`}
        style={{
          // Phone frame is centered (430px wide). Place tooltip ~250px right of center.
          left: 'calc(50% + 245px)',
        }}
      >
        <div className="relative bg-gradient-to-br from-cyan-500/95 to-blue-600/95 backdrop-blur-md rounded-2xl p-5 shadow-2xl border border-white/20 text-white">
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-colors"
            aria-label={t('demo.explain.dismiss', 'Razumijem')}
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
              <Lightbulb className="w-4 h-4" />
            </div>
            <span className="text-[10px] uppercase tracking-wider font-bold opacity-80">
              {t('demo.banner', 'Demo')}
            </span>
          </div>

          <h3 className="text-base font-bold mb-1.5 leading-tight">{title}</h3>
          <p className="text-sm text-white/90 leading-relaxed">{body}</p>
        </div>

        {/* Arrow pointing left to phone frame */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-0 h-0 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-cyan-500/95" />
      </div>
    );
  }

  // Mobile: fixed top banner overlaying the phone frame
  return (
    <div
      key={context}
      className={`fixed top-3 left-3 right-3 ${zIndex} animate-in fade-in slide-in-from-top-2 duration-300`}
    >
      <div className="relative bg-gradient-to-br from-cyan-500/95 to-blue-600/95 backdrop-blur-md rounded-2xl px-4 py-3 shadow-2xl border border-white/20 text-white">
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-1.5 right-1.5 p-1 rounded-full hover:bg-white/20 transition-colors"
          aria-label={t('demo.explain.dismiss', 'Razumijem')}
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="flex items-start gap-2 pr-6">
          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center shrink-0 mt-0.5">
            <Lightbulb className="w-3.5 h-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">{title}</h3>
            <p className="text-[11px] text-white/90 mt-0.5 leading-snug">{body}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DemoExplainTooltip;
